import { useState } from "react";

import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { cn } from "~/lib/utils";
import type { AccountMapping } from "~/types/contabilidad";
import { CLASE_TYPE_COLORS, REPORTE_TYPE_COLORS } from "~/types/contabilidad";

interface MapeoTableProps {
  mappings: AccountMapping[];
  isLoading: boolean;
  onEdit: (mapping: AccountMapping) => void;
  onDelete: (id: string) => void;
}

export function MapeoTable({
  mappings,
  isLoading,
  onEdit,
  onDelete,
}: MapeoTableProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState<AccountMapping | null>(
    null
  );

  const handleDeleteClick = (mapping: AccountMapping) => {
    setMappingToDelete(mapping);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (mappingToDelete) {
      onDelete(mappingToDelete.id);
      setDeleteDialogOpen(false);
      setMappingToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (mappings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">No hay mapeos registrados.</p>
        <p className="text-sm text-muted-foreground">
          Agrega cuentas a esta plantilla o importa desde Excel.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px] whitespace-nowrap">
                Codigo
              </TableHead>
              <TableHead className="min-w-[150px] whitespace-nowrap">
                Cuenta
              </TableHead>
              <TableHead className="w-[80px] whitespace-nowrap">Tipo</TableHead>
              <TableHead className="w-[100px] whitespace-nowrap">
                Reporte
              </TableHead>
              <TableHead className="w-[140px] whitespace-nowrap">
                Clase
              </TableHead>
              <TableHead className="w-[80px] whitespace-nowrap">
                Ord. Clase
              </TableHead>
              <TableHead className="w-[80px] whitespace-nowrap">
                Ord. Tipo
              </TableHead>
              <TableHead className="w-[120px] whitespace-nowrap">
                Sub Clase
              </TableHead>
              <TableHead className="w-[90px] whitespace-nowrap">
                Ord. Subcl.
              </TableHead>
              <TableHead className="w-[150px] whitespace-nowrap">
                SubClase 2
              </TableHead>
              <TableHead className="w-[100px] whitespace-nowrap">
                Ord. Subcl.2
              </TableHead>
              <TableHead className="w-[130px] whitespace-nowrap">
                Grupo Cuenta
              </TableHead>
              <TableHead className="w-[90px] whitespace-nowrap">
                Ord. Grp.Cta
              </TableHead>
              <TableHead className="w-[70px] whitespace-nowrap">
                Clave
              </TableHead>
              <TableHead className="w-[70px] whitespace-nowrap">
                Signo
              </TableHead>
              <TableHead className="w-[100px] whitespace-nowrap">
                Clase 1
              </TableHead>
              <TableHead className="sticky right-0 w-[70px] whitespace-nowrap bg-background">
                Acciones
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mappings.map((mapping, index) => (
              <TableRow
                key={mapping.id}
                className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
              >
                <TableCell className="font-mono font-medium">
                  {mapping.account?.code}
                </TableCell>
                <TableCell className="max-w-[200px] truncate">
                  {mapping.account?.name}
                </TableCell>
                <TableCell className="text-sm">{mapping.tipo || "-"}</TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={cn(REPORTE_TYPE_COLORS[mapping.reporte])}
                  >
                    {mapping.reporte}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={cn(CLASE_TYPE_COLORS[mapping.clase])}
                  >
                    {mapping.clase}
                  </Badge>
                </TableCell>
                <TableCell className="text-center text-sm">
                  {mapping.orden_clase}
                </TableCell>
                <TableCell className="text-center text-sm">
                  {mapping.orden_tipo}
                </TableCell>
                <TableCell className="text-sm">{mapping.sub_clase}</TableCell>
                <TableCell className="text-center text-sm">
                  {mapping.orden_subclase}
                </TableCell>
                <TableCell className="text-sm">{mapping.sub_clase_2}</TableCell>
                <TableCell className="text-center text-sm">
                  {mapping.orden_subclase_2}
                </TableCell>
                <TableCell className="text-sm">
                  {mapping.grupo_cuenta?.name || "-"}
                </TableCell>
                <TableCell className="text-center text-sm">
                  {mapping.orden_grupo_cuenta}
                </TableCell>
                <TableCell className="text-center text-sm">
                  {mapping.clave ?? "-"}
                </TableCell>
                <TableCell className="text-center text-sm">
                  {mapping.signo ?? "-"}
                </TableCell>
                <TableCell className="text-sm">
                  {mapping.clase_1 || "-"}
                </TableCell>
                <TableCell className="sticky right-0 bg-background">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Abrir menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit(mapping)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleDeleteClick(mapping)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar mapeo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion eliminara el mapeo de la cuenta "
              {mappingToDelete?.account?.code} -{" "}
              {mappingToDelete?.account?.name}". Esta accion no se puede
              deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
